import { useEffect, useRef, useState } from "react";
import { Star } from "@phosphor-icons/react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/117cf0fb-3794-49a5-86c5-1fbf8d876a37/images/a088f790bad916d6bdbc78ef1268a3548f5bc7487da9be20eb92f540bf55e0b1.png";

export default function HeroSection() {
  const [offset, setOffset] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setOffset(window.scrollY * 0.35);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section ref={sectionRef} data-testid="hero-section" className="relative min-h-[85vh] sm:min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Training facility"
          className="w-full h-[120%] object-cover animate-drift"
          style={{ transform: `translateY(${-offset * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-[#050505]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </div>

      {/* Content — symmetric 6.5rem top/bottom padding for equal breathing room */}
      <div
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8"
        style={{ paddingTop: '6.5rem', paddingBottom: '6.5rem' }}
      >
        {/* Headline block — locked column width so both lines read as intentional */}
        <div className="animate-fade-in-up stagger-1" style={{ maxWidth: '740px' }}>
          <h1
            className="font-heading font-black text-[#F2EFE9]"
            style={{
              fontSize: 'clamp(3.25rem, 8vw, 5.5rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              margin: 0,
              padding: 0,
            }}
          >
            Build confidence
            <br />
            you can actually use.
          </h1>

          {/* Gold accent — margin-top matches the inter-line visual gap */}
          <span
            className="accent-line"
            style={{ marginTop: '0.75rem', display: 'block' }}
          />
        </div>

        {/* Body — 2rem above mirrors the category-to-headline gap */}
        <p
          className="text-[#D5CFC8] font-body leading-relaxed animate-fade-in-up stagger-2 text-base sm:text-lg"
          style={{ maxWidth: '600px', marginTop: '2rem', marginBottom: '1.5rem' }}
        >
          3SM is a trauma-informed training system built around Strength, Self-Defense, and Striking Mindset. Through private coaching, youth programming, workshops, and organizational training, we help people develop practical confidence, composure, awareness, and real-world readiness.
        </p>

        <p
          data-testid="hero-tagline"
          className="animate-fade-in-up stagger-2 text-[#D4AF37] font-heading font-bold uppercase text-xs sm:text-sm"
          style={{ letterSpacing: '0.22em', marginBottom: '2.5rem' }}
        >
          A System. Not a Place. &nbsp;&mdash;&nbsp; From victim to victory mindset.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
          <button
            data-testid="hero-cta-workshop"
            onClick={() => window.location.href = '/apply?type=workshop'}
            className="bg-[#D4AF37] text-[#050505] font-bold px-14 py-4 rounded-md hover:bg-[#E5C158] transition-all uppercase tracking-widest text-sm animate-breathe cta-glow"
          >
            Join a Workshop
          </button>
          <button
            data-testid="hero-cta-coaching"
            onClick={() => window.location.href = '/apply?type=coaching'}
            className="border-2 border-[#D4AF37] text-[#D4AF37] font-bold px-14 py-4 rounded-md hover:bg-[#D4AF37]/10 transition-all uppercase tracking-widest text-sm"
          >
            Apply for Coaching
          </button>
        </div>

        <div data-testid="hero-google-badge" className="flex items-center gap-3 animate-fade-in-up stagger-3" style={{ marginTop: '1.5rem' }}>
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map(i => <Star key={i} size={16} weight="fill" className="text-[#D4AF37]" />)}
          </div>
          <span className="text-sm text-[#F2EFE9]/80 font-body">
            <span className="font-bold text-[#F2EFE9]">5.0</span> from <span className="font-bold text-[#F2EFE9]">77</span> Five-Star Google Reviews
          </span>
        </div>
      </div>
    </section>
  );
}
